/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MobileLanguageSwitch from '@/features/mobile-app/components/MobileLanguageSwitch/MobileLanguageSwitch'
import BackDrop from '@/features/mobile-app/components/BackDrop/BackDrop'
import useMobileAppManagementQuery from '@/features/mobile-app/hooks/useMobileAppManagementQuery'
import MobileIconButton from '@/features/mobile-app/components/MobileIconButton/MobileIconButton'
import styles from './HomePage.module.scss'
import { useImageSlots } from '@/shared/store/useImageSlots'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'

interface HomePageProps {
  children: React.ReactNode
  isTransparentDrawerOpen: boolean
}

function HomePage({ children, isTransparentDrawerOpen }: HomePageProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const { t } = useTranslation()

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  useEffect(() => {
    if (isTransparentDrawerOpen) {
      setIsMenuOpen(false)
    }
  }, [isTransparentDrawerOpen])

  const { logoImage, backgroundImage } = useMobileAppManagementQuery()

  const newLogoImage = useImageSlots(s => s.slots[IMAGE_SLOTS_KEYS.LOGO_IMAGE])

  const logoImagePreviewUrl =
    newLogoImage?.croppedUrl ||
    (logoImage.data?.imageUrl
      ? `${logoImage.data.imageUrl}?t=${logoImage.data.version}&alt=media`
      : '')

  const newBackgroundImage = useImageSlots(
    s => s.slots[IMAGE_SLOTS_KEYS.MOBILE_APP_BACKGROUND]
  )

  const backgroundImagePreviewUrl =
    newBackgroundImage?.croppedUrl ||
    (backgroundImage.data?.imageUrl
      ? `${backgroundImage.data.imageUrl}?t=${backgroundImage.data.version}&alt=media`
      : '')

  return (
    <>
      <div
        className={styles.backgroundImage}
        style={{
          backgroundImage: backgroundImagePreviewUrl
            ? `url(${backgroundImagePreviewUrl})`
            : 'none'
        }}
      />
      <div
        className={`${styles.headerImage} ${
          isMenuOpen ? styles.headerImageVisible : ''
        }`}
      />
      <div
        className={`${styles.headerImageOverlay} ${
          isMenuOpen ? styles.headerImageOverlayVisible : ''
        }`}
        style={{ opacity: isMenuOpen ? 1 : 0 }}
      />
      <div
        className={styles.venueLogoImage}
        style={{
          backgroundImage: logoImagePreviewUrl
            ? `url(${logoImagePreviewUrl})`
            : 'none'
        }}
      />
      <BackDrop isOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <div className={styles.homePageWrapper}>
        <div className={styles.headerWrapper}>
          <div className={styles.headerButtonsContainer}>
            <MobileIconButton
              iconName={`${
                isMenuOpen ? 'las la-times' : 'las la-bars'
              } icon-big`}
              onClick={toggleMenu}
              colorType={isMenuOpen ? 'secondary' : 'primary'}
            />
            <div
              className={`${styles.languageSwitchContainer} ${
                isMenuOpen ? styles.languageSwitchContainerVisible : ''
              }`}
            >
              <MobileLanguageSwitch />
            </div>
          </div>
          <div
            className={`${styles.headerTextContainer} ${
              isMenuOpen ? styles.headerTextContainerVisible : ''
            }`}
          >
            <p className={`title-bold-large ${styles.headerVenueName}`}>
              {t('MOBILE_APP.HOME_PAGE.HEADER.VENUE_NAME')}
            </p>
            <p className={`body-bold-small ${styles.styledParagraph}`}>
              {t('MOBILE_APP.HOME_PAGE.HEADER.VISIT_WEBSITE')}
            </p>
          </div>
        </div>
        {children}
        <div className={styles.footerContainer}>
          <div className={styles.footerTitleContainer}>
            <p className={`body-small ${styles.footerTitleText}`}>
              {t('MOBILE_APP.HOME_PAGE.FOOTER.TITLE.FIRST_PART')}
            </p>
            <p className={`body-small-logo ${styles.footerTitleText}`}>
              {t('MOBILE_APP.HOME_PAGE.FOOTER.TITLE.SECOND_PART')}
            </p>
          </div>
          <p className={`body-small ${styles.footerDescription}`}>
            {t('MOBILE_APP.HOME_PAGE.FOOTER.DESCRIPTION.FIRST_PART')}
            <mark className="mark-underlined">
              {t('MOBILE_APP.HOME_PAGE.FOOTER.DESCRIPTION.SECOND_PART')}
            </mark>
            {t('MOBILE_APP.HOME_PAGE.FOOTER.DESCRIPTION.THIRD_PART')}
            <mark className="mark-underlined">
              {t('MOBILE_APP.HOME_PAGE.FOOTER.DESCRIPTION.FOURTH_PART')}
            </mark>
            {t('MOBILE_APP.HOME_PAGE.FOOTER.DESCRIPTION.FIFTH_PART')}
          </p>
        </div>
      </div>
    </>
  )
}

export default HomePage
